import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

/**
 * @typedef {import('class-variance-authority').VariantProps<typeof badgeVariants>} BadgeVariantProps
 * @typedef {React.HTMLAttributes<HTMLDivElement> & BadgeVariantProps} BadgeProps
 */

const badgeVariants = cva(
  'inline-flex h-fit w-fit items-center rounded-md border px-2.5 py-0.5 text-xs font-normal transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary-300 text-white hover:bg-primary-200',
        secondary: 'border-transparent bg-gray-50 text-gray-900 hover:bg-gray-200',
        destructive: 'border-transparent bg-red-500 text-white hover:bg-red-600',
        outline: 'text-gray-800 border border-gray-200',
      },
      size: {
        xs: 'text-xs',
        sm: 'text-sm',
        md: 'text-base',
        lg: 'text-lg',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'xs',
    },
  },
);

/**
 * Renders a badge with pre-defined styles and variants.
 * @param {BadgeProps} props
 */
function BadgeComponent({ className, variant, size, ...props }, ref) {
  return (
    <div
      className={twMerge(clsx(badgeVariants({ variant, size }), className))}
      {...props}
      ref={ref}
    />
  );
}

export const Badge = forwardRef(BadgeComponent);
